import React from 'react';
import { format } from 'date-fns';
import {
  DocumentArrowUpIcon,
  ClipboardDocumentCheckIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { getStatusBadge } from '../../utils/documentVerification';

const FormSubmissionsTable = ({
  filteredSubmissions,
  selectedSubmissions,
  onSelectSubmission,
  onSelectAll,
  selectAll,
  onFormNameClick,
  onClientClick,
  onUserClick,
  onDocumentUpload,
  onVerification,
  onCreateProformaInvoice,
  onDelete,
  getStatusIcon,
  getPendingTimeColor,
  calculatePendingTime,
  checkUpdatePermission,
  checkDeletePermission
}) => {
  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* Desktop View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={onSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Form
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Submitted By
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pending Days Left
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Proforma Invoice
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Verification Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredSubmissions.map((submission) => (
              <tr key={submission?._id || `temp-${Math.random()}`}>
                <td className="px-4 py-3 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedSubmissions.has(submission._id)}
                    onChange={() => onSelectSubmission(submission._id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div 
                    className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer font-medium"
                    onClick={() => onFormNameClick(submission)}
                    title={checkUpdatePermission('Form Submissions') ? 'Click to edit' : 'Click to view details'}
                  >
                    {submission?.form?.name || 'Unknown Form'}
                  </div>
                  <div className="text-xs text-gray-500">{submission?.productsData?.length || 0} products</div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {submission?.documentProgress?.uploaded || 0}/{submission?.documentProgress?.total || 0}
                    </span>
                    <button
                      onClick={() => onDocumentUpload(submission)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                    >
                      <DocumentArrowUpIcon className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-500">
                      {submission?.verificationProgress?.completed || 0}/{submission?.verificationProgress?.total || 0}
                    </span>
                    <button
                      onClick={() => onVerification(submission)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                    >
                      <ClipboardDocumentCheckIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div 
                    className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer font-medium"
                    onClick={() => onClientClick(submission?.masterDataId)}
                    title="Click to view client details"
                  >
                    {submission?.masterDataId?.clientName || 'N/A'}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div 
                    className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer font-medium"
                    onClick={() => onUserClick(submission?.submittedBy)}
                    title="Click to view user details"
                  >
                    {submission?.submittedBy?.firstName || ''} {submission?.submittedBy?.lastName || ''}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-start space-x-1">
                    <CalendarIcon className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-900">
                        {submission?.createdAt ? format(new Date(submission.createdAt), 'MMM d, yyyy') : 'N/A'}
                      </div>
                      {submission?.deadlineDate && (
                        <div className={getPendingTimeColor(submission.deadlineDate)}>
                          {calculatePendingTime(submission.deadlineDate)}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-3 whitespace-nowrap">
                  {submission?.proformaInvoice ? (
                    <div className="flex items-center space-x-2">
                      <CurrencyDollarIcon className="h-4 w-4 text-green-500" />
                      <div>
                        <div className="text-sm text-gray-900">{submission.proformaInvoice.invoiceNumber}</div>
                        <div className="text-xs text-gray-500">${submission.proformaInvoice.totalAmount?.toFixed(2)}</div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => onCreateProformaInvoice(submission)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                    >
                      <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                      Create
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    {getStatusIcon(submission?.status)}
                    <span className="ml-2 text-sm text-gray-900">
                      {submission?.status || 'Pending'}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <ShieldCheckIcon className="h-4 w-4 text-gray-400" />
                    <div className="group relative">
                      <div className="text-sm text-gray-900 cursor-help">
                        {submission?.verificationProgress?.completed || 0}/{submission?.verificationProgress?.total || 0} Verified
                      </div>
                      {submission?.verificationProgress?.total > 0 && (
                        <div className="text-xs text-gray-500">
                          {Math.round(((submission?.verificationProgress?.completed || 0) / submission?.verificationProgress?.total) * 100)}% Complete
                        </div>
                      )}
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        <div className="text-center">
                          <div className="font-medium">Checklist Status</div>
                          <div className="mt-1">
                            {submission?.verificationProgress?.total === 0 ? (
                              'No documents require verification'
                            ) : (
                              <>
                                {submission?.verificationProgress?.completed || 0} of {submission?.verificationProgress?.total} documents verified
                                <br />
                                {submission?.verificationProgress?.total - (submission?.verificationProgress?.completed || 0)} pending verification
                              </>
                            )}
                          </div>
                        </div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    {checkDeletePermission && (
                      <button
                        onClick={() => onDelete(submission)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                        title="Delete submission"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        <div className="divide-y divide-gray-200">
          {filteredSubmissions.map((submission) => (
            <div key={submission?._id || `temp-${Math.random()}`} className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedSubmissions.has(submission._id)}
                    onChange={() => onSelectSubmission(submission._id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                  />
                  <div>
                    <h3 
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      onClick={() => onFormNameClick(submission)}
                      title={checkUpdatePermission('Form Submissions') ? 'Click to edit' : 'Click to view details'}
                    >
                      {submission?.form?.name || 'Unknown Form'}
                    </h3>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div 
                        className="text-blue-600 hover:text-blue-800 cursor-pointer"
                        onClick={() => onClientClick(submission?.masterDataId)}
                        title="Click to view client details"
                      >
                        Client: {submission?.masterDataId?.clientName || 'N/A'}
                      </div>
                      <div 
                        className="text-blue-600 hover:text-blue-800 cursor-pointer"
                        onClick={() => onUserClick(submission?.submittedBy)}
                        title="Click to view user details"
                      >
                        By: {submission?.submittedBy?.firstName || ''} {submission?.submittedBy?.lastName || ''}
                      </div>
                    </div>
                  </div>
                </div>
                {checkDeletePermission && (
                  <button
                    onClick={() => onDelete(submission)}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                    title="Delete submission"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="mt-2 space-y-2">
                <div className="flex items-center text-sm">
                  <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                  <span className="text-gray-500">
                    {submission?.createdAt ? format(new Date(submission.createdAt), 'MMM d, yyyy') : 'N/A'}
                  </span>
                </div>

                {submission?.deadlineDate && (
                  <div className="flex items-center text-sm">
                    <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                    <span className={getPendingTimeColor(submission.deadlineDate)}>
                      {calculatePendingTime(submission.deadlineDate)}
                    </span>
                  </div>
                )}

                <div className="flex items-center text-sm">
                  <DocumentArrowUpIcon className="h-4 w-4 text-gray-400 mr-1" />
                  <span className="text-gray-500">
                    {submission?.documentProgress?.uploaded || 0}/{submission?.documentProgress?.total || 0} Documents
                  </span>
                </div>

                <div className="flex items-center text-sm">
                  <ClipboardDocumentCheckIcon className="h-4 w-4 text-gray-400 mr-1" />
                  <span className="text-gray-500">
                    {submission?.verificationProgress?.completed || 0}/{submission?.verificationProgress?.total || 0} Verifications
                  </span>
                </div>

                <div className="flex items-center text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${submission?.status === 'approved' ? 'bg-green-100 text-green-800' :
                      submission?.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                    }`}>
                    {submission?.status?.charAt(0).toUpperCase() + submission?.status?.slice(1) || 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FormSubmissionsTable; 